import {
  Accordion,
  AvatarChip,
  Button,
  cn,
  Icon,
  Spinner,
} from '@risksmart-app/atomic-ui';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { SECTION_IDS } from '../constants';
import type { DetailsSectionProps } from '../types';

const MAX_VISIBLE_OWNERS = 4;

const DetailsLabel = ({ children }: { children: ReactNode }) => (
  <span
    className={cn('text-sm font-semibold text-primary-hover whitespace-nowrap')}
  >
    {children}
  </span>
);

const DetailsRow = ({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) => (
  <div
    className={cn('flex flex-1 min-w-0 flex-col gap-1.5 overflow-hidden')}
    data-testid={testId}
  >
    <DetailsLabel>{label}</DetailsLabel>
    <div className={cn('text-base font-semibold text-foreground min-w-0')}>
      {children}
    </div>
  </div>
);

const EmptyValue = ({ label }: { label: string }) => (
  <span className={cn('font-normal text-muted-foreground')}>{label}</span>
);

const DetailsSection = ({ lang, data, loading }: DetailsSectionProps) => {
  const [showMore, setShowMore] = useState(false);

  // Reset the expanded state when the owner set changes (e.g. the panel is
  // switched to a different risk, or the current risk's owners are edited)
  // so a fresh card never starts already-expanded.
  const ownerIdsKey = data.owners.map((o) => o.id).join('|');
  useEffect(() => {
    setShowMore(false);
  }, [ownerIdsKey]);

  if (loading) {
    return (
      <div className={cn('p-4 flex justify-center')}>
        <Spinner />
      </div>
    );
  }

  const visibleOwners = showMore
    ? data.owners
    : data.owners.slice(0, MAX_VISIBLE_OWNERS);

  return (
    <Accordion
      multiple
      defaultValue={[SECTION_IDS.details]}
      className={cn('gap-0')}
    >
      <Accordion.Item
        value={SECTION_IDS.details}
        className={cn('border-none')}
        data-testid={'details-section'}
      >
        <Accordion.Header className={cn('border-none')}>
          <Accordion.Trigger
            className={cn('bg-neutral p-0 mx-3 min-h-[43px] items-center')}
          >
            <div className={cn('flex items-center gap-2')}>
              <span
                className={cn('h-4 w-0.5 shrink-0 rounded-full bg-secondary')}
                aria-hidden={true}
              />
              <span className={cn('text-base font-semibold text-foreground')}>
                {lang.title}
              </span>
            </div>
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content className={cn('p-0 px-4 pb-3')}>
          <div className={cn('flex flex-col gap-3')}>
            {data.entity ? (
              <DetailsRow
                label={lang.entityLabel}
                testId={'details-row-entity'}
              >
                {data.entity.displayName ? (
                  <span data-testid={'details-entity-name'}>
                    {data.entity.displayName}
                  </span>
                ) : (
                  <EmptyValue label={lang.emptyValue} />
                )}
              </DetailsRow>
            ) : null}
            <DetailsRow label={lang.statusLabel} testId={'details-row-status'}>
              {data.status && lang.statusLabels[data.status] ? (
                <span data-testid={'details-status-name'}>
                  {lang.statusLabels[data.status]}
                </span>
              ) : (
                <EmptyValue label={lang.emptyValue} />
              )}
            </DetailsRow>
            <div className={cn('flex w-full items-start gap-3')}>
              <DetailsRow
                label={lang.modifiedByLabel}
                testId={'details-row-modified-by'}
              >
                {data.lastModifiedByName ?? (
                  <EmptyValue label={lang.emptyValue} />
                )}
              </DetailsRow>
              <DetailsRow
                label={lang.modifiedAtLabel}
                testId={'details-row-modified-at'}
              >
                {data.lastModifiedAt ? (
                  <span className={cn('flex items-center gap-1.5')}>
                    <Icon
                      name={'calendar'}
                      size={'sm'}
                      className={cn('shrink-0 stroke-2')}
                    />
                    <span className={cn('truncate')}>
                      {data.lastModifiedAt}
                    </span>
                  </span>
                ) : (
                  <EmptyValue label={lang.emptyValue} />
                )}
              </DetailsRow>
            </div>
            <div
              className={cn('flex w-full flex-col gap-1.5')}
              data-testid={'details-row-owners'}
            >
              <DetailsLabel>{`${lang.ownersLabel} (${data.owners.length})`}</DetailsLabel>
              {data.owners.length === 0 ? (
                <EmptyValue label={lang.emptyValue} />
              ) : (
                <>
                  <div className={cn('grid grid-cols-2 gap-1.5')}>
                    {visibleOwners.map((owner) => (
                      <AvatarChip
                        key={`${owner.type}-${owner.id}`}
                        name={owner.name}
                        variant={owner.type === 'userGroup' ? 'group' : 'user'}
                        data-testid={'details-owner-chip'}
                      />
                    ))}
                  </div>
                  {data.owners.length > MAX_VISIBLE_OWNERS && (
                    <Button
                      variant={'neutral'}
                      style={'ghost'}
                      size={'sm'}
                      onClick={() => setShowMore(!showMore)}
                      data-testid={'details-owners-toggle'}
                    >
                      {showMore ? lang.showFewerLabel : lang.showMoreLabel}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion>
  );
};

export { DetailsSection };
